import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type PickupWindow = {
  dayOfWeek:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  startTime: string; // "HH:MM" 24-hour
  endTime: string;
};

const DAY_ORDER: PickupWindow["dayOfWeek"][] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_LABEL: Record<PickupWindow["dayOfWeek"], string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

function formatTimeForDisplay(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const hr12 = h % 12 === 0 ? 12 : h % 12;
  return `${hr12}:${String(m).padStart(2, "0")} ${period}`;
}

function sortWindows(windows: PickupWindow[]): PickupWindow[] {
  return [...windows].sort((a, b) => {
    const da = DAY_ORDER.indexOf(a.dayOfWeek);
    const db = DAY_ORDER.indexOf(b.dayOfWeek);
    if (da !== db) return da - db;
    return a.startTime.localeCompare(b.startTime);
  });
}

export function PickupWindowsCard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [draft, setDraft] = useState<PickupWindow[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery<{ windows: PickupWindow[] }>({
    queryKey: ["/api/me/pickup-windows"],
  });

  useEffect(() => {
    if (data && !dirty) {
      setDraft(sortWindows(data.windows ?? []));
    }
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: async (windows: PickupWindow[]) => {
      const res = await apiRequest("PUT", "/api/me/pickup-windows", { windows });
      return res.json() as Promise<{ windows: PickupWindow[] }>;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["/api/me/pickup-windows"], result);
      setDirty(false);
      toast({ title: "Driver pickup schedule saved" });
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't save schedule", description: err.message, variant: "destructive" }),
  });

  function addWindow() {
    setDirty(true);
    setDraft((d) =>
      sortWindows([
        ...d,
        { dayOfWeek: "saturday", startTime: "09:00", endTime: "13:00" },
      ]),
    );
  }

  function removeWindow(idx: number) {
    setDirty(true);
    setDraft((d) => d.filter((_, i) => i !== idx));
  }

  function updateWindow(idx: number, patch: Partial<PickupWindow>) {
    setDirty(true);
    setDraft((d) =>
      d.map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    );
  }

  // Reject windows where end <= start so the save button only fires
  // valid input — server schema validates the same way.
  const hasInvalid = draft.some((w) => w.startTime >= w.endTime);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Driver pickup schedule (for community delivery)
        </CardTitle>
        <CardDescription>
          For community delivery only — this is when a driver can come to you to
          collect orders and take them to buyers. Buyers never see it. Drivers
          pick a 2-hour slot inside one of these windows, within 7 days of the
          buyer's order. If you leave this empty, we default to Saturday
          8 AM–12 PM. To set the pickup and delivery choices buyers actually
          select, use "Pickup & Delivery Options" below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : draft.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No windows set yet — add one below.
          </p>
        ) : (
          draft.map((window, idx) => (
            <div
              key={idx}
              className="flex flex-wrap items-end gap-2 rounded-md border p-3"
            >
              <div className="flex-1 min-w-[120px]">
                <Label className="text-xs">Day</Label>
                <Select
                  value={window.dayOfWeek}
                  onValueChange={(v) => updateWindow(idx, { dayOfWeek: v as PickupWindow["dayOfWeek"] })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_ORDER.map((day) => (
                      <SelectItem key={day} value={day}>
                        {DAY_LABEL[day]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[110px]">
                <Label className="text-xs">From</Label>
                <Input
                  type="time"
                  value={window.startTime}
                  onChange={(e) => updateWindow(idx, { startTime: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex-1 min-w-[110px]">
                <Label className="text-xs">Until</Label>
                <Input
                  type="time"
                  value={window.endTime}
                  onChange={(e) => updateWindow(idx, { endTime: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeWindow(idx)}
                aria-label="Remove window"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              {window.startTime >= window.endTime && (
                <p className="basis-full text-xs text-destructive">
                  End time must be after start time.
                </p>
              )}
            </div>
          ))
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={addWindow}>
            <Plus className="mr-1 h-4 w-4" />
            Add window
          </Button>
          <Button
            size="sm"
            onClick={() => save.mutate(draft)}
            disabled={!dirty || hasInvalid || save.isPending}
          >
            {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Save schedule
          </Button>
          {dirty && !save.isPending && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDirty(false);
                setDraft(sortWindows(data?.windows ?? []));
              }}
            >
              Discard
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
